import type React from 'react';
import { createContext, useContext, useState, useReducer, useEffect, type ReactNode } from 'react';
import type { GlobalSearchFilters } from '../../pages/search/components/GlobalSearch';
import { filterReducer, type FilterState, type FilterAction } from './SimpleFilterContext';
import type { Id } from '../../../convex/_generated/dataModel'; // Adjust path as needed

// Enhanced search context that combines old and new filtering approaches
interface SearchContextType {
  // Legacy filters (maintain backward compatibility)
  filters: GlobalSearchFilters;
  setFilters: (filters: GlobalSearchFilters) => void;
  
  // New filter state and dispatch
  filterState: FilterState;
  filterDispatch: React.Dispatch<FilterAction>;
  
  // View mode
  viewMode: 'list' | 'grid' | 'masonry';
  setViewMode: (mode: 'list' | 'grid' | 'masonry') => void;
  
  // Computed values
  isSearchActive: boolean;
  hasActiveFilters: boolean;
  
  // Adapter functions for transitioning between old and new formats
  syncFromLegacyFilters: (legacyFilters: GlobalSearchFilters) => void;
  syncToLegacyFilters: () => GlobalSearchFilters;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}

interface SearchProviderProps {
  children: ReactNode;
}

// Helper function to convert legacy filters to new filter state
function convertLegacyToNewFilters(legacyFilters: GlobalSearchFilters): Partial<FilterState> {
  const newState: Partial<FilterState> = {};
  
  // Convert search text
  if (legacyFilters.searchText) {
    newState.searchTerm = legacyFilters.searchText;
  }
  
  // Convert tags
  if (legacyFilters.tags && legacyFilters.tags.length > 0) {
    const tags: FilterState['tags'] = {};
    
    legacyFilters.tags.forEach((tag: { id: Id<'tags'>; name: string; negative?: boolean }) => {
      const mode = tag.negative ? 'excluded' : 'inclusive';
      const tagId = `${mode}-${tag.name}`;
      tags[tagId] = {
        value: tag.name,
        mode,
        category: undefined
      };
    });
    
    newState.tags = tags;
  }
  
  return newState;
}

// Helper function to convert new filter state to legacy filters
function convertNewToLegacyFilters(filterState: FilterState, allTags: Array<{ _id: Id<'tags'>; name: string }> = []): GlobalSearchFilters {
  const legacyFilters: GlobalSearchFilters = {
    searchText: filterState.searchTerm || '',
    tags: [],
    apps: [],
    rawQuery: ''
  };
  
  // Convert tags back to legacy format
  Object.values(filterState.tags).forEach(tag => {
    // Find the tag ID from the available tags
    const matchingTag = allTags.find(t => t.name === tag.value);
    if (matchingTag) {
      legacyFilters.tags.push({
        id: matchingTag._id,
        name: tag.value,
        negative: tag.mode === 'excluded'
      });
    }
  });
  
  // Build raw query for backward compatibility
  const queryParts: string[] = [];
  if (legacyFilters.searchText) {
    queryParts.push(legacyFilters.searchText);
  }
  
  legacyFilters.tags.forEach((tag) => {
    queryParts.push(`${tag.negative ? '-' : ''}tag:${tag.name}`);
  });
  
  legacyFilters.rawQuery = queryParts.join(' ');
  
  return legacyFilters;
}

export function SearchProvider({ children }: SearchProviderProps) {
  // Legacy state (for backward compatibility)
  const [filters, setFilters] = useState<GlobalSearchFilters>({
    searchText: '',
    tags: [],
    apps: [],
    rawQuery: ''
  });
  
  // New filter state
  const initialFilterState: FilterState = {
    tags: {},
    searchTerm: '',
    globalLogic: 'AND'
  };
  
  const [filterState, filterDispatch] = useReducer(filterReducer, initialFilterState);
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'masonry'>('grid');
  
  // URL synchronization effect (from SimpleFilterContext)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initialState: Partial<FilterState> = {};
    
    // Parse search term
    const searchTerm = urlParams.get('q');
    if (searchTerm) {
      initialState.searchTerm = searchTerm;
    }
    
    // Parse global logic
    const globalLogic = urlParams.get('logic');
    if (globalLogic === 'AND' || globalLogic === 'OR') {
      initialState.globalLogic = globalLogic;
    }
    
    // Parse tags
    const tagParams = urlParams.getAll('tag');
    const tags: FilterState['tags'] = {};
    
    tagParams.forEach(tagParam => {
      const [mode, ...valueParts] = tagParam.split(':');
      const value = valueParts.join(':'); // Handle values with colons
      
      if ((mode === 'inclusive' || mode === 'required' || mode === 'excluded') && value) {
        const tagId = `${mode}-${value}`;
        tags[tagId] = { value, mode };
      }
    });
    
    if (Object.keys(tags).length > 0) {
      initialState.tags = tags;
    }
    
    // Initialize state if we have URL parameters
    if (Object.keys(initialState).length > 0) {
      filterDispatch({ type: 'INITIALIZE_FROM_URL', state: initialState });
    }
  }, []);
  
  // Update URL when filter state changes
  useEffect(() => {
    const params = new URLSearchParams();
    
    // Add tags to URL
    Object.entries(filterState.tags).forEach(([tagId, { value, mode }]) => {
      params.append('tag', `${mode}:${value}`);
    });
    
    // Add search term (with null safety)
    if (filterState.searchTerm && filterState.searchTerm.trim()) {
      params.set('q', filterState.searchTerm.trim());
    }
    
    // Add global logic (only if not default AND)
    if (filterState.globalLogic === 'OR') {
      params.set('logic', filterState.globalLogic);
    }
    
    // Update URL without triggering navigation
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, [filterState]);
  
  // Sync from legacy filters to new filter state
  const syncFromLegacyFilters = (legacyFilters: GlobalSearchFilters) => {
    const newState = convertLegacyToNewFilters(legacyFilters);
    if (Object.keys(newState).length > 0) {
      filterDispatch({ type: 'INITIALIZE_FROM_URL', state: newState });
    }
  };
  
  // Sync from new filter state to legacy filters
  const syncToLegacyFilters = () => {
    return convertNewToLegacyFilters(filterState);
  };
  
  // Computed values
  const isSearchActive = !!(
    filters.searchText || 
    filters.tags.length > 0 || 
    filters.apps.length > 0 ||
    filterState.searchTerm ||
    Object.keys(filterState.tags).length > 0
  );
  
  const hasActiveFilters = Object.keys(filterState.tags).length > 0 || !!(filterState.searchTerm && filterState.searchTerm.trim().length > 0);

  return (
    <SearchContext.Provider
      value={{
        // Legacy
        filters,
        setFilters,
        
        // New
        filterState,
        filterDispatch,
        
        // View mode
        viewMode,
        setViewMode,
        
        // Computed
        isSearchActive,
        hasActiveFilters,
        
        // Adapters
        syncFromLegacyFilters,
        syncToLegacyFilters
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

// Export the filter types for use in other components
export type { FilterState, FilterAction };
export { filterReducer };

// Helper function to check if any filters are active (from SimpleFilterContext)
export const hasActiveFilters = (state: FilterState): boolean => {
  return Object.keys(state.tags).length > 0 || !!(state.searchTerm && state.searchTerm.trim().length > 0);
};

// Helper function to get tag count by mode (from SimpleFilterContext)
export const getTagCountByMode = (state: FilterState) => {
  const counts = { inclusive: 0, required: 0, excluded: 0 };
  Object.values(state.tags).forEach(tag => {
    counts[tag.mode]++;
  });
  return counts;
}; 